import { useState, useMemo } from 'react';
import {
  useCashRegisterStatus,
  useCashRegisters,
  useCashRegister,
  useOpenCashRegister,
  useCloseCashRegister,
  useReopenCashRegister,
  useProfitHistory,
  useCashWithdrawals,
  useCreateCashWithdrawal,
  useUpdateCashWithdrawal,
  useDeleteCashWithdrawal,
  useWithdrawalCategories,
  useCreateWithdrawalCategory,
  useUpdateWithdrawalCategory,
  useDeleteWithdrawalCategory,
} from '@/hooks/useCashRegister';
import { useAuthStore } from '@/context/authStore';
import { CashWithdrawal, CashWithdrawalCategory, PaymentMethod, CloseType, WithdrawalCategory, WithdrawalCategoryKind, WITHDRAWAL_CATEGORY_LABELS, WITHDRAWAL_KIND_LABELS, PAYMENT_METHOD_LABELS } from '@/services/cashRegister';
import { formatPrice, formatDate, formatDateTime } from '@/utils/format';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import {
  DollarSign,
  Lock,
  Unlock,
  TrendingUp,
  CreditCard,
  Banknote,
  ArrowRightLeft,
  AlertCircle,
  CheckCircle,
  History,
  Clock,
  MinusCircle,
  Plus,
  Trash2,
  Truck,
  Car,
  ShoppingBag,
  Wrench,
  Wallet,
  Store,
  MoreHorizontal,
  RotateCcw,
  Eye,
  User,
  Users,
  Pencil,
  Tags,
} from 'lucide-react';
import clsx from 'clsx';

// Iconos disponibles para las categorías (el dueño elige uno al crearlas)
const ICONS_BY_NAME: Record<string, React.ElementType> = {
  Truck,
  Car,
  ShoppingBag,
  Wrench,
  Wallet,
  Store,
  Users,
  MoreHorizontal,
  Banknote,
  CreditCard,
  DollarSign,
};

// Iconos de las categorías que trae el sistema, como respaldo para el historial viejo
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  delivery: Truck,
  transport: Car,
  supplies: ShoppingBag,
  services: Wrench,
  withdrawal: Wallet,
  supplier_payment: Store,
  salary: Users,
  other: MoreHorizontal,
};

export default function CashRegister() {
  const { user } = useAuthStore();
  // El superadmin impersonando entra con rol superadmin en el store
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenRegisterId, setReopenRegisterId] = useState<number | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [viewRegisterId, setViewRegisterId] = useState<number | null>(null);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [openingNotes, setOpeningNotes] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [closeType, setCloseType] = useState<CloseType>('final');

  // Estados para salidas de caja
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalCategory, setWithdrawalCategory] = useState<CashWithdrawalCategory>('other');
  const [withdrawalPaymentMethod, setWithdrawalPaymentMethod] = useState<PaymentMethod>('cash');
  const [withdrawalDescription, setWithdrawalDescription] = useState('');
  const [withdrawalRecipient, setWithdrawalRecipient] = useState('');
  const [deleteWithdrawalId, setDeleteWithdrawalId] = useState<number | null>(null);
  const [editingWithdrawal, setEditingWithdrawal] = useState<CashWithdrawal | null>(null);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  const { data: status, isLoading } = useCashRegisterStatus();
  const { data: history } = useCashRegisters({ limit: 100 });
  const { data: profitHistory } = useProfitHistory(30);
  const { data: withdrawals = [] } = useCashWithdrawals(undefined, status?.is_open);

  // Para ver detalle de una caja del historial
  const { data: viewRegisterData } = useCashRegister(viewRegisterId);
  const { data: viewRegisterWithdrawals = [] } = useCashWithdrawals(viewRegisterId || undefined, !!viewRegisterId);
  const openRegister = useOpenCashRegister();
  const closeRegister = useCloseCashRegister();
  const reopenRegister = useReopenCashRegister();
  const createWithdrawal = useCreateCashWithdrawal();
  const deleteWithdrawal = useDeleteCashWithdrawal();

  // Categorías de salida de la veterinaria (el dueño las crea y edita)
  const { data: categories = [] } = useWithdrawalCategories();
  const categoryBySlug = useMemo(() => {
    const map = new Map<string, WithdrawalCategory>();
    categories.forEach((c) => map.set(c.slug, c));
    return map;
  }, [categories]);

  // Nombre e icono de una categoría, con respaldo para el historial viejo
  const labelOf = (slug: string) =>
    categoryBySlug.get(slug)?.name || WITHDRAWAL_CATEGORY_LABELS[slug] || slug;
  const iconOf = (slug: string): React.ElementType => {
    const iconName = categoryBySlug.get(slug)?.icon;
    return (iconName && ICONS_BY_NAME[iconName]) || CATEGORY_ICONS[slug] || MoreHorizontal;
  };

  // Las que se pueden elegir al cargar una salida (pago a proveedor lo maneja Compras)
  const selectableCategories = useMemo(
    () => categories.filter((c) => c.kind !== 'supplier_payment' && c.is_active),
    [categories]
  );

  // Si la veterinaria borró o renombró la categoría por defecto, caer en la primera suya
  const effectiveCategory = selectableCategories.some((c) => c.slug === withdrawalCategory)
    ? withdrawalCategory
    : selectableCategories[0]?.slug ?? withdrawalCategory;

  const handleOpenRegister = async () => {
    if (!openingCash) return;
    try {
      await openRegister.mutateAsync({
        opening_cash: parseFloat(openingCash),
        opening_notes: openingNotes || undefined,
      });
      setShowOpenModal(false);
      setOpeningCash('');
      setOpeningNotes('');
    } catch (error) {
      console.error('Error opening register:', error);
    }
  };

  // Paso 1: Abrir modal de confirmación
  const handleRequestClose = () => {
    if (!closingCash) return;
    setShowCloseModal(false);
    setShowConfirmCloseModal(true);
  };

  // Paso 2: Confirmar y cerrar caja
  const handleCloseRegister = async () => {
    if (!closingCash || !status?.current_register) return;
    try {
      await closeRegister.mutateAsync({
        id: status.current_register.id,
        data: {
          closing_cash: parseFloat(closingCash),
          closing_notes: closingNotes || undefined,
          close_type: closeType,
        },
      });
      setShowConfirmCloseModal(false);
      setClosingCash('');
      setClosingNotes('');
      setCloseType('final');
    } catch (error) {
      console.error('Error closing register:', error);
    }
  };

  // Cancelar confirmación y volver al modal anterior
  const handleCancelConfirm = () => {
    setShowConfirmCloseModal(false);
    setShowCloseModal(true);
  };

  // Handler para crear salida de caja
  const handleCreateWithdrawal = async () => {
    if (!withdrawalAmount) return;
    try {
      await createWithdrawal.mutateAsync({
        amount: parseFloat(withdrawalAmount),
        category: effectiveCategory,
        payment_method: withdrawalPaymentMethod,
        description: withdrawalDescription || undefined,
        recipient: withdrawalRecipient || undefined,
      });
      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setWithdrawalCategory('other');
      setWithdrawalPaymentMethod('cash');
      setWithdrawalDescription('');
      setWithdrawalRecipient('');
    } catch (error) {
      console.error('Error creating withdrawal:', error);
    }
  };

  // Handler para eliminar salida de caja
  const handleDeleteWithdrawal = async () => {
    if (!deleteWithdrawalId) return;
    try {
      await deleteWithdrawal.mutateAsync(deleteWithdrawalId);
      setDeleteWithdrawalId(null);
    } catch (error) {
      console.error('Error deleting withdrawal:', error);
    }
  };

  // Handler para reabrir caja (solo admin)
  const handleRequestReopen = (registerId: number) => {
    setReopenRegisterId(registerId);
    setShowReopenModal(true);
  };

  const handleReopenRegister = async () => {
    if (!reopenRegisterId) return;
    try {
      await reopenRegister.mutateAsync(reopenRegisterId);
      setShowReopenModal(false);
      setReopenRegisterId(null);
      setShowHistoryModal(false);
    } catch (error) {
      console.error('Error reopening register:', error);
    }
  };

  const currentRegister = status?.current_register;
  // Total de TODAS las salidas (para mostrar en UI)
  const totalWithdrawals = currentRegister?.total_withdrawals || 0;
  const totalExpenses = currentRegister?.total_expenses || 0;
  // Efectivo esperado = inicial + ventas efectivo - salidas de efectivo SOLAMENTE
  const totalCashWithdrawals = currentRegister?.total_cash_withdrawals || 0;
  const expectedCash = currentRegister
    ? Number(currentRegister.opening_cash) + Number(currentRegister.total_cash_sales) - Number(totalCashWithdrawals)
    : 0;

  // Separar gastos, sueldos, pagos a proveedores y retiros SEGÚN EL TIPO de cada categoría,
  // que ahora configura la veterinaria. Lo que no esté en la lista se muestra como gasto,
  // igual que antes hacía el sistema con cualquier categoría desconocida.
  const kindOf = (slug: string) => categoryBySlug.get(slug)?.kind ?? 'expense';
  const expenseWithdrawals = withdrawals.filter((w) => kindOf(w.category) === 'expense');
  const salaryWithdrawals = withdrawals.filter((w) => kindOf(w.category) === 'salary');
  const supplierPaymentWithdrawals = withdrawals.filter((w) => kindOf(w.category) === 'supplier_payment');
  const merchandiseWithdrawals = withdrawals.filter((w) => kindOf(w.category) === 'merchandise');
  const safeWithdrawals = withdrawals.filter((w) => kindOf(w.category) === 'withdrawal');
  const totalSalaries = salaryWithdrawals.reduce((sum, w) => sum + Number(w.amount), 0);
  const totalSupplierPayments = supplierPaymentWithdrawals.reduce((sum, w) => sum + Number(w.amount), 0);
  const totalMerchandise = merchandiseWithdrawals.reduce((sum, w) => sum + Number(w.amount), 0);
  const totalSafeWithdrawals = safeWithdrawals.reduce((sum, w) => sum + Number(w.amount), 0);

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Caja Diaria</h1>
          <p className="text-gray-600">Control de apertura y cierre de caja</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowHistoryModal(true)}>
            <History className="h-4 w-4 mr-2" />
            Historial
          </Button>
          {isAdmin && (
            <Button variant="secondary" onClick={() => setShowCategoriesModal(true)}>
              <Tags className="h-4 w-4 mr-2" />
              Categorías
            </Button>
          )}
          {status?.is_open && (
            <Button variant="secondary" onClick={() => setShowWithdrawalModal(true)}>
              <MinusCircle className="h-4 w-4 mr-2" />
              Registrar Salida
            </Button>
          )}
          {status?.is_open ? (
            <Button variant="primary" onClick={() => setShowCloseModal(true)}>
              <Lock className="h-4 w-4 mr-2" />
              Cerrar Caja
            </Button>
          ) : (
            <Button onClick={() => setShowOpenModal(true)}>
              <Unlock className="h-4 w-4 mr-2" />
              Abrir Caja
            </Button>
          )}
        </div>
      </div>

      {/* Estado actual */}
      <div className={clsx(
        'rounded-2xl p-6 border-2',
        status?.is_open
          ? 'bg-green-50 border-green-200'
          : 'bg-gray-50 border-gray-200'
      )}>
        <div className="flex items-center gap-3 mb-4">
          {status?.is_open ? (
            <>
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                <Unlock className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-green-800">
                  Caja Abierta
                  {currentRegister && currentRegister.shift_number > 1 && (
                    <span className="ml-2 text-sm font-normal bg-green-200 text-green-700 px-2 py-0.5 rounded-full">
                      Turno {currentRegister.shift_number}
                    </span>
                  )}
                </h2>
                <p className="text-sm text-green-600">
                  Abierta el {formatDateTime(currentRegister?.opened_at)}
                  {currentRegister?.opened_by && ` por ${currentRegister.opened_by.full_name}`}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-12 h-12 bg-gray-400 rounded-xl flex items-center justify-center">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-700">Caja Cerrada</h2>
                <p className="text-sm text-gray-500">
                  {status?.last_closed_register
                    ? `Última caja cerrada el ${formatDateTime(status.last_closed_register.closed_at)}`
                    : 'No hay registros de caja'}
                  {status?.last_closed_register?.auto_closed && (
                    <span className="ml-2 text-amber-600">(Cierre automático)</span>
                  )}
                </p>
              </div>
              {/* Botón reabrir para admin */}
              {isAdmin && status?.last_closed_register && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRequestReopen(status.last_closed_register!.id)}
                  className="ml-4"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reabrir Caja
                </Button>
              )}
            </>
          )}
        </div>

        {/* Resumen de caja abierta */}
        {currentRegister && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white rounded-xl p-4 border border-green-100">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <Banknote className="h-5 w-5" />
                <span className="text-sm font-medium">Efectivo Inicial</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(currentRegister.opening_cash)}
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <DollarSign className="h-5 w-5" />
                <span className="text-sm font-medium">Ventas Totales</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(currentRegister.total_sales)}
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-emerald-100">
              <div className="flex items-center gap-2 text-emerald-600 mb-2">
                <TrendingUp className="h-5 w-5" />
                <span className="text-sm font-medium">Ganancia</span>
              </div>
              <p className={`text-2xl font-bold ${currentRegister.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatPrice(currentRegister.total_profit)}
              </p>
              {totalExpenses > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Neta (gastos: -{formatPrice(totalExpenses)})
                </p>
              )}
            </div>

            <div className="bg-white rounded-xl p-4 border border-amber-100">
              <div className="flex items-center gap-2 text-amber-600 mb-2">
                <Banknote className="h-5 w-5" />
                <span className="text-sm font-medium">Efectivo Esperado</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(expectedCash)}
              </p>
            </div>
          </div>
        )}

        {/* Desglose por método de pago */}
        {currentRegister && (
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Banknote className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Efectivo</p>
                  <p className="font-bold text-gray-900">{formatPrice(currentRegister.total_cash_sales)}</p>
                </div>
              </div>
              {(currentRegister.total_cash_withdrawals || 0) > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-red-600">
                  Salidas: -{formatPrice(currentRegister.total_cash_withdrawals)}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Tarjeta</p>
                  <p className="font-bold text-gray-900">{formatPrice(currentRegister.total_card_sales)}</p>
                </div>
              </div>
              {(currentRegister.total_card_withdrawals || 0) > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-red-600">
                  Salidas: -{formatPrice(currentRegister.total_card_withdrawals)}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ArrowRightLeft className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Transferencia</p>
                  <p className="font-bold text-gray-900">{formatPrice(currentRegister.total_transfer_sales)}</p>
                </div>
              </div>
              {(currentRegister.total_transfer_withdrawals || 0) > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-red-600">
                  Salidas: -{formatPrice(currentRegister.total_transfer_withdrawals)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Gastos (afectan ganancia) */}
        {currentRegister && expenseWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-red-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-red-600">
                <MinusCircle className="h-5 w-5" />
                <span className="font-medium">Gastos</span>
              </div>
              <span className="text-xl font-bold text-red-600">-{formatPrice(totalExpenses)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">Restan de la ganancia</p>
            <div className="space-y-2">
              {expenseWithdrawals.map((w) => {
                const Icon = iconOf(w.category);
                return (
                  <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                        <Icon className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{w.description || labelOf(w.category)}</p>
                        <p className="text-xs text-gray-500">
                          {labelOf(w.category)}
                          {w.payment_method && ` • ${PAYMENT_METHOD_LABELS[w.payment_method]}`}
                          {w.recipient && ` • ${w.recipient}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-red-600">-{formatPrice(w.amount)}</span>
                      <button
                        onClick={() => setEditingWithdrawal(w)}
                        className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteWithdrawalId(w.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pagos a Proveedores (NO afectan ganancia, solo saldo de caja) */}
        {currentRegister && supplierPaymentWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-amber-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-amber-700">
                <Store className="h-5 w-5" />
                <span className="font-medium">Pagos a Proveedores</span>
              </div>
              <span className="text-xl font-bold text-amber-700">-{formatPrice(totalSupplierPayments)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">Reducen el saldo de caja, no afectan la ganancia</p>
            <div className="space-y-2">
              {supplierPaymentWithdrawals.map((w) => (
                <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                      <Store className="h-4 w-4 text-amber-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{w.description || labelOf(w.category)}</p>
                      <p className="text-xs text-gray-500">
                        {PAYMENT_METHOD_LABELS[w.payment_method]}
                        {w.recipient && ` • ${w.recipient}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-amber-700">-{formatPrice(w.amount)}</span>
                    <button
                      onClick={() => setDeleteWithdrawalId(w.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mercadería / insumos: bajan la caja pero NO son gasto */}
        {currentRegister && merchandiseWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-orange-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-orange-700">
                <ShoppingBag className="h-5 w-5" />
                <span className="font-medium">Mercadería e insumos</span>
              </div>
              <span className="text-xl font-bold text-orange-700">-{formatPrice(totalMerchandise)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Reducen el saldo de caja, no afectan la ganancia: el costo impacta al vender
            </p>
            <div className="space-y-2">
              {merchandiseWithdrawals.map((w) => {
                const Icon = iconOf(w.category);
                return (
                  <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                        <Icon className="h-4 w-4 text-orange-700" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{w.description || labelOf(w.category)}</p>
                        <p className="text-xs text-gray-500">
                          {labelOf(w.category)}
                          {w.payment_method && ` • ${PAYMENT_METHOD_LABELS[w.payment_method]}`}
                          {w.recipient && ` • ${w.recipient}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-orange-700">-{formatPrice(w.amount)}</span>
                      <button
                        onClick={() => setEditingWithdrawal(w)}
                        className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteWithdrawalId(w.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sueldos (afectan ganancia) */}
        {currentRegister && salaryWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-indigo-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-indigo-700">
                <Users className="h-5 w-5" />
                <span className="font-medium">Sueldos</span>
              </div>
              <span className="text-xl font-bold text-indigo-700">-{formatPrice(totalSalaries)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">Afectan la ganancia del negocio</p>
            <div className="space-y-2">
              {salaryWithdrawals.map((w) => (
                <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center">
                      <Users className="h-4 w-4 text-indigo-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{w.recipient || 'Sueldo'}</p>
                      <p className="text-xs text-gray-500">
                        {PAYMENT_METHOD_LABELS[w.payment_method]}
                        {w.description && ` • ${w.description}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-indigo-700">-{formatPrice(w.amount)}</span>
                    <button
                      onClick={() => setEditingWithdrawal(w)}
                      className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteWithdrawalId(w.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Retiros (NO afectan ganancia) */}
        {currentRegister && safeWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-blue-600">
                <Banknote className="h-5 w-5" />
                <span className="font-medium">Retiros</span>
              </div>
              <span className="text-xl font-bold text-blue-600">-{formatPrice(totalSafeWithdrawals)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">No afectan la ganancia</p>
            <div className="space-y-2">
              {safeWithdrawals.map((w) => {
                const Icon = iconOf(w.category);
                return (
                  <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Icon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{w.description || labelOf(w.category)}</p>
                        <p className="text-xs text-gray-500">
                          {labelOf(w.category)}
                          {w.payment_method && ` • ${PAYMENT_METHOD_LABELS[w.payment_method]}`}
                          {w.recipient && ` • ${w.recipient}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-blue-600">-{formatPrice(w.amount)}</span>
                      <button
                        onClick={() => setEditingWithdrawal(w)}
                        className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteWithdrawalId(w.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Historial de ganancias */}
      {profitHistory && profitHistory.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Historial de Ganancias
            </h3>
          </div>
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ventas</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Costo</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gastos</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ganancia</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Dif. Caja</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {profitHistory.slice(0, 10).map((day, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(day.date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatPrice(day.total_sales)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">
                        {formatPrice(day.total_cost)}
                      </td>
                      <td className="px-4 py-3 text-sm text-red-500 text-right">
                        {(day.total_expenses || 0) > 0 ? `-${formatPrice(day.total_expenses)}` : '-'}
                      </td>
                      <td className={clsx(
                        'px-4 py-3 text-sm font-medium text-right',
                        day.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'
                      )}>
                        {formatPrice(day.total_profit)}
                      </td>
                      <td className={clsx(
                        'px-4 py-3 text-sm text-right',
                        day.cash_difference === 0
                          ? 'text-gray-500'
                          : day.cash_difference > 0
                          ? 'text-blue-600'
                          : 'text-red-600'
                      )}>
                        {day.cash_difference > 0 && '+'}
                        {formatPrice(day.cash_difference)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal Abrir Caja */}
      <Modal
        isOpen={showOpenModal}
        onClose={() => setShowOpenModal(false)}
        title={status?.can_open_new_shift ? `Abrir Turno ${status.next_shift_number}` : "Abrir Caja"}
      >
        <div className="space-y-4">
          {/* Info del turno anterior si hay cierre parcial */}
          {status?.last_partial_close_today && (
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-200 text-amber-800 text-sm font-bold">
                  {status.last_partial_close_today.shift_number}
                </span>
                <div>
                  <span className="font-medium text-amber-800">Turno {status.last_partial_close_today.shift_number} finalizado</span>
                  <p className="text-xs text-amber-600">Cierre parcial del día</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-white/50 rounded-lg p-2">
                  <p className="text-xs text-amber-600">Efectivo al cierre</p>
                  <p className="font-bold text-amber-800">{formatPrice(status.last_partial_close_today.closing_cash || 0)}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-2">
                  <p className="text-xs text-amber-600">Ventas del turno</p>
                  <p className="font-bold text-amber-800">{formatPrice(status.last_partial_close_today.total_sales || 0)}</p>
                </div>
              </div>
              <p className="text-xs text-amber-700 mt-2">
                Puede usar el efectivo de cierre como base para el nuevo turno o ingresar un monto diferente.
              </p>
            </div>
          )}

          <div className="p-4 bg-blue-50 rounded-xl">
            <div className="flex items-center gap-3">
              {status?.can_open_new_shift && (
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-200 text-blue-800 text-lg font-bold">
                  {status.next_shift_number}
                </span>
              )}
              <p className="text-sm text-blue-800">
                {status?.can_open_new_shift
                  ? `Va a iniciar el turno ${status.next_shift_number} del día. Ingrese el efectivo inicial.`
                  : 'Ingrese el monto de efectivo con el que inicia la caja del día.'}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Efectivo Inicial
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                placeholder="0,00"
                min="0"
                step="0.01"
                className="w-full pl-8 pr-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all duration-200 bg-white text-taupe-800 placeholder-taupe-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Notas (opcional)
            </label>
            <textarea
              value={openingNotes}
              onChange={(e) => setOpeningNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              placeholder="Observaciones de apertura..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowOpenModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleOpenRegister}
              loading={openRegister.isPending}
              disabled={!openingCash}
            >
              <Unlock className="h-4 w-4 mr-2" />
              Abrir Caja
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Cerrar Caja */}
      <Modal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        title={currentRegister && currentRegister.shift_number > 1
          ? `Cerrar Turno ${currentRegister.shift_number}`
          : "Cerrar Caja"}
        size="lg"
      >
        <div className="space-y-4">
          {/* Info del turno actual */}
          {currentRegister && currentRegister.shift_number > 1 && (
            <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-2 text-purple-800">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-200 text-purple-800 text-xs font-bold">
                  {currentRegister.shift_number}
                </span>
                <span className="text-sm font-medium">
                  Estás cerrando el turno {currentRegister.shift_number} del día
                </span>
              </div>
            </div>
          )}

          {/* Resumen antes de cerrar */}
          {currentRegister && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Efectivo Inicial</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatPrice(currentRegister.opening_cash)}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl">
                <p className="text-sm text-green-600 mb-1">Ventas en Efectivo</p>
                <p className="text-xl font-bold text-green-700">
                  +{formatPrice(currentRegister.total_cash_sales)}
                </p>
              </div>
              {totalWithdrawals > 0 && (
                <div className="p-4 bg-red-50 rounded-xl col-span-2">
                  <p className="text-sm text-red-600 mb-1">Salidas de Caja</p>
                  <p className="text-xl font-bold text-red-700">
                    -{formatPrice(totalWithdrawals)}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 text-blue-800 mb-1">
              <DollarSign className="h-5 w-5" />
              <span className="font-medium">Efectivo Esperado en Caja</span>
            </div>
            <p className="text-3xl font-bold text-blue-900">
              {formatPrice(expectedCash)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Efectivo Real en Caja
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                value={closingCash}
                onChange={(e) => setClosingCash(e.target.value)}
                placeholder="0,00"
                min="0"
                step="0.01"
                className="w-full pl-8 pr-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all duration-200 bg-white text-taupe-800 placeholder-taupe-400"
              />
            </div>
          </div>

          {closingCash && (
            <div className={clsx(
              'p-4 rounded-xl flex items-center gap-3',
              parseFloat(closingCash) === expectedCash
                ? 'bg-green-50 border border-green-200'
                : parseFloat(closingCash) > expectedCash
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-red-50 border border-red-200'
            )}>
              {parseFloat(closingCash) === expectedCash ? (
                <>
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Caja Cuadrada</p>
                    <p className="text-sm text-green-600">El efectivo coincide con lo esperado</p>
                  </div>
                </>
              ) : parseFloat(closingCash) > expectedCash ? (
                <>
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">Sobrante</p>
                    <p className="text-sm text-blue-600">
                      {formatPrice(parseFloat(closingCash) - expectedCash)} de más
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="h-6 w-6 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Faltante</p>
                    <p className="text-sm text-red-600">
                      {formatPrice(expectedCash - parseFloat(closingCash))} de menos
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Notas de Cierre (opcional)
            </label>
            <textarea
              value={closingNotes}
              onChange={(e) => setClosingNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              placeholder="Observaciones de cierre..."
            />
          </div>

          {/* Tipo de cierre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Cómo desea cerrar{currentRegister && currentRegister.shift_number > 1 ? ` el turno ${currentRegister.shift_number}` : ''}?
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCloseType('partial')}
                className={clsx(
                  'p-4 rounded-xl border-2 text-left transition-all',
                  closeType === 'partial'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Clock className={clsx('h-5 w-5', closeType === 'partial' ? 'text-orange-600' : 'text-gray-400')} />
                  <span className={clsx('font-medium', closeType === 'partial' ? 'text-orange-800' : 'text-gray-700')}>
                    Cierre Parcial
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Finaliza este turno. Se podrá abrir el turno {(currentRegister?.shift_number || 1) + 1} después.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setCloseType('final')}
                className={clsx(
                  'p-4 rounded-xl border-2 text-left transition-all',
                  closeType === 'final'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Lock className={clsx('h-5 w-5', closeType === 'final' ? 'text-red-600' : 'text-gray-400')} />
                  <span className={clsx('font-medium', closeType === 'final' ? 'text-red-800' : 'text-gray-700')}>
                    Cierre Final
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Cierre del día completo. No más turnos hoy.
                </p>
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowCloseModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleRequestClose}
              disabled={!closingCash}
            >
              <Lock className="h-4 w-4 mr-2" />
              Continuar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Confirmación de Cierre */}
      <Modal
        isOpen={showConfirmCloseModal}
        onClose={handleCancelConfirm}
        title={closeType === 'partial'
          ? `Confirmar Cierre de Turno${currentRegister?.shift_number ? ` ${currentRegister.shift_number}` : ''}`
          : 'Confirmar Cierre Final del Día'}
      >
        <div className="space-y-4">
          <div className={clsx(
            'p-4 border rounded-xl',
            closeType === 'partial'
              ? 'bg-amber-50 border-amber-200'
              : 'bg-red-50 border-red-200'
          )}>
            <div className="flex items-start gap-3">
              {closeType === 'partial' ? (
                <Clock className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={clsx(
                  'font-medium',
                  closeType === 'partial' ? 'text-amber-800' : 'text-red-800'
                )}>
                  {closeType === 'partial'
                    ? '¿Confirmar cierre de turno?'
                    : '¿Confirmar cierre final del día?'}
                </p>
                <p className={clsx(
                  'text-sm mt-1',
                  closeType === 'partial' ? 'text-amber-700' : 'text-red-700'
                )}>
                  {closeType === 'partial'
                    ? 'Podrá abrir otro turno después de este cierre.'
                    : 'No podrá abrir más turnos hoy después de este cierre.'}
                </p>
              </div>
            </div>
          </div>

          {/* Resumen final */}
          {currentRegister && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Efectivo Inicial:</span>
                <span className="font-medium">{formatPrice(currentRegister.opening_cash)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Ventas en Efectivo:</span>
                <span className="font-medium text-green-600">+{formatPrice(currentRegister.total_cash_sales)}</span>
              </div>
              {totalWithdrawals > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Salidas de Caja:</span>
                  <span className="font-medium text-red-600">-{formatPrice(totalWithdrawals)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm border-t pt-2">
                <span className="text-gray-600">Efectivo Esperado:</span>
                <span className="font-bold">{formatPrice(expectedCash)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Efectivo Declarado:</span>
                <span className="font-bold">{formatPrice(parseFloat(closingCash) || 0)}</span>
              </div>
              {closingCash && parseFloat(closingCash) !== expectedCash && (
                <div className={clsx(
                  'flex justify-between text-sm pt-2 border-t',
                  parseFloat(closingCash) > expectedCash ? 'text-blue-600' : 'text-red-600'
                )}>
                  <span>Diferencia:</span>
                  <span className="font-bold">
                    {parseFloat(closingCash) > expectedCash ? '+' : ''}
                    {formatPrice(parseFloat(closingCash) - expectedCash)}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={handleCancelConfirm}>
              Volver
            </Button>
            <Button
              onClick={handleCloseRegister}
              loading={closeRegister.isPending}
              className={closeType === 'partial' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {closeType === 'partial' ? (
                <Clock className="h-4 w-4 mr-2" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              {closeType === 'partial' ? 'Cerrar Turno' : 'Cierre Final'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Registrar Salida */}
      <Modal
        isOpen={showWithdrawalModal}
        onClose={() => setShowWithdrawalModal(false)}
        title="Registrar Salida de Caja"
      >
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 rounded-xl">
            <p className="text-sm text-amber-800">
              Registre los gastos o retiros de efectivo realizados durante el día.
              Esto se descontará del efectivo esperado al cierre.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Monto
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                placeholder="0,00"
                min="0"
                step="0.01"
                className="w-full pl-8 pr-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all duration-200 bg-white text-taupe-800 placeholder-taupe-400"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Categoría</span>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setShowCategoriesModal(true)}
                  className="text-xs text-primary-600 hover:text-primary-800"
                >
                  Administrar categorías
                </button>
              )}
            </div>
            <Select
              label=""
              value={effectiveCategory}
              onChange={(e) => {
                const val = e.target.value as CashWithdrawalCategory;
                setWithdrawalCategory(val);
                if (categoryBySlug.get(val)?.kind !== 'salary') setWithdrawalRecipient('');
              }}
              options={selectableCategories.map((c) => ({
                value: c.slug,
                label: `${c.name} · ${WITHDRAWAL_KIND_LABELS[c.kind]}`,
              }))}
            />
          </div>

          {categoryBySlug.get(effectiveCategory)?.kind === 'salary' ? (
            <Input
              label="¿A quién?"
              value={withdrawalRecipient}
              onChange={(e) => setWithdrawalRecipient(e.target.value)}
              placeholder="Nombre de la persona (opcional)"
            />
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Método de Pago
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setWithdrawalPaymentMethod('cash')}
                className={clsx(
                  'flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all',
                  withdrawalPaymentMethod === 'cash'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                )}
              >
                <Banknote className="h-5 w-5 mb-1" />
                <span className="text-xs font-medium">Efectivo</span>
              </button>
              <button
                type="button"
                onClick={() => setWithdrawalPaymentMethod('card')}
                className={clsx(
                  'flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all',
                  withdrawalPaymentMethod === 'card'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                )}
              >
                <CreditCard className="h-5 w-5 mb-1" />
                <span className="text-xs font-medium">Tarjeta</span>
              </button>
              <button
                type="button"
                onClick={() => setWithdrawalPaymentMethod('transfer')}
                className={clsx(
                  'flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all',
                  withdrawalPaymentMethod === 'transfer'
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                )}
              >
                <ArrowRightLeft className="h-5 w-5 mb-1" />
                <span className="text-xs font-medium">Transferencia</span>
              </button>
            </div>
          </div>

          <Input
            label="Descripción (opcional)"
            value={withdrawalDescription}
            onChange={(e) => setWithdrawalDescription(e.target.value)}
            placeholder="Ej: Pago cadete envío zona sur"
          />

          <Input
            label="Destinatario (opcional)"
            value={withdrawalRecipient}
            onChange={(e) => setWithdrawalRecipient(e.target.value)}
            placeholder="Ej: Juan Pérez"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowWithdrawalModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateWithdrawal}
              loading={createWithdrawal.isPending}
              disabled={!withdrawalAmount}
            >
              <Plus className="h-4 w-4 mr-2" />
              Registrar Salida
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Historial */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title="Historial de Cajas"
        size="full"
      >
        <div className="overflow-auto">
          {history && history.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Turno</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo Cierre</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Apertura</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cierre</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ventas</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ganancia</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Diferencia</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.map((register) => (
                  <tr key={register.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {formatDate(register.opened_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={clsx(
                        'inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold',
                        register.shift_number === 1
                          ? 'bg-blue-100 text-blue-700'
                          : register.shift_number === 2
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-indigo-100 text-indigo-700'
                      )}>
                        {register.shift_number || 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className={clsx(
                          'px-2 py-1 rounded-full text-xs font-medium',
                          register.status === 'open'
                            ? 'bg-green-100 text-green-700'
                            : register.auto_closed
                            ? 'bg-amber-100 text-amber-700'
                            : register.close_type === 'partial'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-700'
                        )}>
                          {register.status === 'open'
                            ? 'Abierta'
                            : register.auto_closed
                            ? 'Auto'
                            : register.close_type === 'partial'
                            ? 'Parcial'
                            : 'Final'}
                        </span>
                        {register.auto_closed && (
                          <span title="Cierre automático">
                            <Clock className="h-3 w-3 text-amber-600" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatPrice(register.opening_cash)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {register.closing_cash ? formatPrice(register.closing_cash) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatPrice(register.total_sales)}
                    </td>
                    <td className={clsx(
                      'px-4 py-3 text-sm font-medium text-right',
                      register.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'
                    )}>
                      {formatPrice(register.total_profit)}
                    </td>
                    <td className={clsx(
                      'px-4 py-3 text-sm text-right',
                      register.cash_difference == null
                        ? 'text-gray-400'
                        : register.cash_difference === 0
                        ? 'text-gray-500'
                        : register.cash_difference > 0
                        ? 'text-blue-600'
                        : 'text-red-600'
                    )}>
                      {register.cash_difference != null
                        ? `${register.cash_difference > 0 ? '+' : ''}${formatPrice(register.cash_difference)}`
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setViewRegisterId(register.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                        title="Ver detalle"
                      >
                        <Eye className="h-3 w-3" />
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No hay registros de caja anteriores
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Confirmar Eliminación de Salida */}
      <Modal
        isOpen={deleteWithdrawalId !== null}
        onClose={() => setDeleteWithdrawalId(null)}
        title="Eliminar Salida"
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-800">¿Eliminar esta salida de caja?</p>
                <p className="text-sm text-red-600 mt-1">
                  Esta acción no se puede deshacer. El monto será sumado nuevamente al efectivo esperado.
                </p>
              </div>
            </div>
          </div>

          {deleteWithdrawalId && (() => {
            const withdrawal = withdrawals.find(w => w.id === deleteWithdrawalId);
            if (!withdrawal) return null;
            const Icon = iconOf(withdrawal.category);
            return (
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {withdrawal.description || labelOf(withdrawal.category)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {labelOf(withdrawal.category)}
                      {withdrawal.recipient && ` • ${withdrawal.recipient}`}
                    </p>
                  </div>
                  <p className="text-xl font-bold text-red-600">
                    -{formatPrice(withdrawal.amount)}
                  </p>
                </div>
              </div>
            );
          })()}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setDeleteWithdrawalId(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteWithdrawal}
              loading={deleteWithdrawal.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Administrar Categorías */}
      <Modal
        isOpen={showCategoriesModal}
        onClose={() => setShowCategoriesModal(false)}
        title="Categorías de salidas de caja"
        size="lg"
      >
        <WithdrawalCategoriesManager />
      </Modal>

      {/* Modal Editar Salida */}
      <Modal
        isOpen={editingWithdrawal !== null}
        onClose={() => setEditingWithdrawal(null)}
        title="Editar Salida"
      >
        {editingWithdrawal && (
          <EditWithdrawalForm
            withdrawal={editingWithdrawal}
            onClose={() => setEditingWithdrawal(null)}
          />
        )}
      </Modal>

      {/* Modal Confirmar Reapertura de Caja */}
      <Modal
        isOpen={showReopenModal}
        onClose={() => {
          setShowReopenModal(false);
          setReopenRegisterId(null);
        }}
        title="Reabrir Caja"
      >
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RotateCcw className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-800">¿Reabrir esta caja?</p>
                <p className="text-sm text-amber-700 mt-1">
                  La caja se reabrirá y podrá continuar registrando ventas y salidas.
                  Los datos de cierre se eliminarán.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Solo el administrador puede reabrir cajas cerradas.
              Use esta función si la caja se cerró por error o necesita agregar transacciones.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowReopenModal(false);
                setReopenRegisterId(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReopenRegister}
              loading={reopenRegister.isPending}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reabrir Caja
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Ver Detalle de Caja */}
      <Modal
        isOpen={viewRegisterId !== null}
        onClose={() => setViewRegisterId(null)}
        title={`Detalle de Caja${viewRegisterData?.shift_number && viewRegisterData.shift_number > 1 ? ` - Turno ${viewRegisterData.shift_number}` : ''}`}
        size="full"
      >
        {viewRegisterData && (
          <div className="space-y-6">
            {/* Info de apertura y cierre */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-5 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 text-green-700 mb-3">
                  <Unlock className="h-5 w-5" />
                  <span className="font-medium">Apertura</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatDateTime(viewRegisterData.opened_at)}</p>
                {viewRegisterData.opened_by && (
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-2">
                    <User className="h-4 w-4" />
                    {viewRegisterData.opened_by.full_name}
                  </p>
                )}
                {viewRegisterData.opening_notes && (
                  <p className="text-sm text-gray-500 mt-3 italic">"{viewRegisterData.opening_notes}"</p>
                )}
              </div>

              <div className="p-5 bg-red-50 rounded-xl border border-red-200">
                <div className="flex items-center gap-2 text-red-700 mb-3">
                  <Lock className="h-5 w-5" />
                  <span className="font-medium">
                    Cierre {viewRegisterData.close_type === 'partial' ? 'Parcial' : 'Final'}
                    {viewRegisterData.auto_closed && ' (Auto)'}
                  </span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {viewRegisterData.closed_at ? formatDateTime(viewRegisterData.closed_at) : '-'}
                </p>
                {viewRegisterData.closed_by && (
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-2">
                    <User className="h-4 w-4" />
                    {viewRegisterData.closed_by.full_name}
                  </p>
                )}
                {viewRegisterData.closing_notes && (
                  <p className="text-sm text-gray-500 mt-3 italic">"{viewRegisterData.closing_notes}"</p>
                )}
              </div>
            </div>

            {/* Resumen financiero */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Efectivo Inicial</p>
                <p className="text-2xl font-bold text-gray-900">{formatPrice(viewRegisterData.opening_cash)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-blue-600 mb-2">Ventas Totales</p>
                <p className="text-2xl font-bold text-blue-700">{formatPrice(viewRegisterData.total_sales)}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <p className="text-sm text-emerald-600 mb-2">Ganancia</p>
                <p className={`text-2xl font-bold ${(viewRegisterData.total_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatPrice(viewRegisterData.total_profit)}</p>
                {(viewRegisterData.total_expenses || 0) > 0 && (
                  <p className="text-xs text-gray-500 mt-1">Gastos: -{formatPrice(viewRegisterData.total_expenses)}</p>
                )}
              </div>
              <div className={clsx(
                'rounded-xl p-4 border',
                viewRegisterData.cash_difference === 0
                  ? 'bg-gray-50 border-gray-200'
                  : (viewRegisterData.cash_difference || 0) > 0
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-red-50 border-red-200'
              )}>
                <p className="text-sm text-gray-500 mb-2">Diferencia</p>
                <p className={clsx(
                  'text-2xl font-bold',
                  viewRegisterData.cash_difference === 0
                    ? 'text-gray-700'
                    : (viewRegisterData.cash_difference || 0) > 0
                    ? 'text-blue-700'
                    : 'text-red-700'
                )}>
                  {(viewRegisterData.cash_difference || 0) > 0 && '+'}
                  {formatPrice(viewRegisterData.cash_difference || 0)}
                </p>
              </div>
            </div>

            {/* Desglose por método de pago */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h4 className="font-medium text-gray-700 mb-4">Desglose de Ventas</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Banknote className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-500">Efectivo</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{formatPrice(viewRegisterData.total_cash_sales)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-500">Tarjeta</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{formatPrice(viewRegisterData.total_card_sales)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowRightLeft className="h-5 w-5 text-purple-600" />
                    <span className="text-sm text-gray-500">Transferencia</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{formatPrice(viewRegisterData.total_transfer_sales)}</p>
                </div>
              </div>
            </div>

            {/* Cálculo de caja */}
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
              <h4 className="font-medium text-amber-700 mb-4">Cálculo de Caja</h4>
              <div className="space-y-3">
                <div className="flex justify-between text-base">
                  <span className="text-gray-600">Efectivo inicial</span>
                  <span className="font-medium">{formatPrice(viewRegisterData.opening_cash)}</span>
                </div>
                <div className="flex justify-between text-base text-green-700">
                  <span>+ Ventas en efectivo</span>
                  <span className="font-medium">+{formatPrice(viewRegisterData.total_cash_sales)}</span>
                </div>
                {(viewRegisterData.total_cash_withdrawals || 0) > 0 && (
                  <div className="flex justify-between text-base text-red-700">
                    <span>- Salidas de efectivo</span>
                    <span className="font-medium">-{formatPrice(viewRegisterData.total_cash_withdrawals)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-amber-300 pt-3 text-base">
                  <span className="font-medium text-gray-700">= Efectivo esperado</span>
                  <span className="font-bold text-lg">{formatPrice(viewRegisterData.expected_cash || 0)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-600">Efectivo declarado</span>
                  <span className="font-medium">{formatPrice(viewRegisterData.closing_cash || 0)}</span>
                </div>
              </div>
            </div>

            {/* Gastos (afectan ganancia) */}
            {viewRegisterWithdrawals.filter((w) => !['withdrawal', 'supplier_payment', 'merchandise'].includes(kindOf(w.category))).length > 0 && (
              <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-red-700 flex items-center gap-2">
                    <MinusCircle className="h-5 w-5" />
                    Gastos
                  </h4>
                  <span className="text-lg font-bold text-red-700">-{formatPrice(viewRegisterData.total_expenses || 0)}</span>
                </div>
                <p className="text-xs text-red-600 mb-4">Restan de la ganancia</p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {viewRegisterWithdrawals.filter((w) => !['withdrawal', 'supplier_payment', 'merchandise'].includes(kindOf(w.category))).map((w) => {
                    const Icon = iconOf(w.category);
                    return (
                      <div key={w.id} className="flex items-center justify-between bg-white rounded-xl p-3 border border-red-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                            <Icon className="h-5 w-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {w.description || labelOf(w.category)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {PAYMENT_METHOD_LABELS[w.payment_method]}
                              {w.recipient && ` • ${w.recipient}`}
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-medium text-red-600">-{formatPrice(w.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pagos a Proveedores (NO afectan ganancia) */}
            {viewRegisterWithdrawals.filter((w) => kindOf(w.category) === 'supplier_payment').length > 0 && (
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-amber-700 flex items-center gap-2">
                    <Store className="h-5 w-5" />
                    Pagos a Proveedores
                  </h4>
                  <span className="text-lg font-bold text-amber-700">
                    -{formatPrice(viewRegisterWithdrawals.filter((w) => kindOf(w.category) === 'supplier_payment').reduce((sum, w) => sum + Number(w.amount), 0))}
                  </span>
                </div>
                <p className="text-xs text-amber-600 mb-4">Reducen saldo de caja, no afectan la ganancia</p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {viewRegisterWithdrawals.filter((w) => kindOf(w.category) === 'supplier_payment').map((w) => (
                    <div key={w.id} className="flex items-center justify-between bg-white rounded-xl p-3 border border-amber-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                          <Store className="h-5 w-5 text-amber-700" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {w.description || labelOf(w.category)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {PAYMENT_METHOD_LABELS[w.payment_method]}
                            {w.recipient && ` • ${w.recipient}`}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-medium text-amber-700">-{formatPrice(w.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Retiros (NO afectan ganancia) */}
            {viewRegisterWithdrawals.filter((w) => kindOf(w.category) === 'withdrawal').length > 0 && (
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-blue-700 flex items-center gap-2">
                    <Banknote className="h-5 w-5" />
                    Retiros
                  </h4>
                  <span className="text-lg font-bold text-blue-700">
                    -{formatPrice(viewRegisterWithdrawals.filter((w) => kindOf(w.category) === 'withdrawal').reduce((sum, w) => sum + Number(w.amount), 0))}
                  </span>
                </div>
                <p className="text-xs text-blue-600 mb-4">No afectan la ganancia</p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {viewRegisterWithdrawals.filter((w) => kindOf(w.category) === 'withdrawal').map((w) => {
                    const Icon = iconOf(w.category);
                    return (
                      <div key={w.id} className="flex items-center justify-between bg-white rounded-xl p-3 border border-blue-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                            <Icon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {w.description || labelOf(w.category)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {PAYMENT_METHOD_LABELS[w.payment_method]}
                              {w.recipient && ` • ${w.recipient}`}
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-medium text-blue-600">-{formatPrice(w.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={() => setViewRegisterId(null)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/**
 * Edición de una salida de caja ya registrada.
 *
 * Sirve para corregir errores de carga (el caso típico: un sueldo cargado como efectivo
 * cuando en realidad se transfirió, que deja el arqueo esperando plata que nunca salió
 * de la caja). Si la caja ya está cerrada, el backend recalcula los totales del cierre,
 * pero sólo lo permite al admin.
 */
function EditWithdrawalForm({
  withdrawal,
  onClose,
}: {
  withdrawal: CashWithdrawal;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState(String(Number(withdrawal.amount)));
  const [category, setCategory] = useState<CashWithdrawalCategory>(withdrawal.category);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(withdrawal.payment_method || 'cash');
  const [description, setDescription] = useState(withdrawal.description || '');
  const [recipient, setRecipient] = useState(withdrawal.recipient || '');
  const [error, setError] = useState<string | null>(null);

  const updateWithdrawal = useUpdateCashWithdrawal();

  // Las categorías de la veterinaria. Si la salida quedó en una categoría que después se
  // desactivó, se agrega igual a la lista para no perderla al guardar.
  const { data: categories = [] } = useWithdrawalCategories(true);
  const categoryOptions = useMemo(() => {
    const options = categories
      .filter((c) => c.kind !== 'supplier_payment' && (c.is_active || c.slug === withdrawal.category))
      .map((c) => ({ value: c.slug, label: `${c.name} · ${WITHDRAWAL_KIND_LABELS[c.kind]}` }));
    if (!options.some((o) => o.value === withdrawal.category)) {
      options.push({ value: withdrawal.category, label: withdrawal.category });
    }
    return options;
  }, [categories, withdrawal.category]);

  const handleSave = async () => {
    const parsed = parseFloat(amount.replace(',', '.'));
    if (!parsed || parsed <= 0) {
      setError('El monto tiene que ser mayor a cero');
      return;
    }
    setError(null);
    try {
      await updateWithdrawal.mutateAsync({
        id: withdrawal.id,
        data: {
          amount: parsed,
          category,
          payment_method: paymentMethod,
          description: description || undefined,
          recipient: recipient || undefined,
        },
      });
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo guardar el cambio');
    }
  };

  const methodOptions: { value: PaymentMethod; label: string; icon: typeof Banknote; active: string }[] = [
    { value: 'cash', label: 'Efectivo', icon: Banknote, active: 'border-green-500 bg-green-50 text-green-700' },
    { value: 'card', label: 'Tarjeta', icon: CreditCard, active: 'border-blue-500 bg-blue-50 text-blue-700' },
    { value: 'transfer', label: 'Transferencia', icon: ArrowRightLeft, active: 'border-purple-500 bg-purple-50 text-purple-700' },
  ];

  return (
    <div className="space-y-4">
      <Input
        label="Monto"
        type="number"
        step="any"
        min="0"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />

      <Select
        label="Categoría"
        value={category}
        onChange={(e) => setCategory(e.target.value as CashWithdrawalCategory)}
        options={categoryOptions}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago</label>
        <div className="grid grid-cols-3 gap-2">
          {methodOptions.map(({ value, label, icon: Icon, active }) => (
            <button
              key={value}
              type="button"
              onClick={() => setPaymentMethod(value)}
              className={clsx(
                'flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all',
                paymentMethod === value ? active : 'border-gray-200 hover:border-gray-300 text-gray-600'
              )}
            >
              <Icon className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">{label}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          El efectivo esperado del arqueo sólo descuenta las salidas en efectivo.
        </p>
      </div>

      <Input
        label="Descripción (opcional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <Input
        label="Destinatario (opcional)"
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
      />

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleSave} loading={updateWithdrawal.isPending}>
          <Pencil className="h-4 w-4 mr-2" />
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}

/**
 * Administración de las categorías de salida de caja de la veterinaria.
 *
 * El dueño arma su propia lista (Reserva, Sueldo vendedor, Sueldo encargado, lo que sea)
 * y elige de qué tipo es cada una. Ese tipo es lo que define si el monto afecta la
 * ganancia: Gasto y Sueldo sí, Retiro no. El slug con el que quedan guardadas las salidas
 * no cambia nunca, así que renombrar una categoría reetiqueta también el historial.
 */
function WithdrawalCategoriesManager() {
  const { data: categories = [] } = useWithdrawalCategories(true);
  const createCategory = useCreateWithdrawalCategory();
  const updateCategory = useUpdateWithdrawalCategory();
  const deleteCategory = useDeleteWithdrawalCategory();

  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<WithdrawalCategoryKind>('expense');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const kindOptions = [
    { value: 'expense', label: 'Gasto (afecta la ganancia)' },
    { value: 'salary', label: 'Sueldo (afecta la ganancia, se reporta aparte)' },
    { value: 'merchandise', label: 'Mercadería (no es gasto: el costo llega al vender)' },
    { value: 'withdrawal', label: 'Retiro (no afecta la ganancia)' },
  ];

  const run = async (fn: () => Promise<unknown>, okMessage?: string) => {
    setError(null);
    setNotice(null);
    try {
      const result: any = await fn();
      setNotice(result?.message || okMessage || null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo guardar el cambio');
    }
  };

  const handleCreate = () => {
    if (!newName.trim()) {
      setError('Poné un nombre para la categoría');
      return;
    }
    run(
      () => createCategory.mutateAsync({ name: newName.trim(), kind: newKind }),
      'Categoría creada'
    ).then(() => setNewName(''));
  };

  return (
    <div className="space-y-5">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
        El <strong>tipo</strong> define cómo impacta en los números: <strong>Gasto</strong> y{' '}
        <strong>Sueldo</strong> descuentan de la ganancia; <strong>Retiro</strong> y{' '}
        <strong>Mercadería</strong> sólo bajan el saldo de caja. La mercadería no es un gasto
        sino plata cambiada por bienes: su costo impacta recién cuando se vende.
      </div>

      {/* Alta */}
      <div className="p-4 border border-gray-200 rounded-xl space-y-3">
        <p className="font-medium text-gray-800">Nueva categoría</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Nombre"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Ej: Reserva, Sueldo vendedor, Encargado"
          />
          <Select
            label="Tipo"
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as WithdrawalCategoryKind)}
            options={kindOptions}
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={handleCreate} loading={createCategory.isPending}>
            <Plus className="h-4 w-4 mr-2" />
            Agregar
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}
      {notice && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{notice}</div>
      )}

      {/* Listado */}
      <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
        {categories.map((cat) => (
          <div key={cat.id} className="flex flex-wrap items-center gap-3 p-3">
            <div className="flex-1 min-w-[160px]">
              <Input
                label=""
                defaultValue={cat.name}
                disabled={cat.is_system}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && value !== cat.name) {
                    run(() => updateCategory.mutateAsync({ id: cat.id, data: { name: value } }), 'Nombre actualizado');
                  }
                }}
              />
            </div>

            <div className="w-full md:w-64">
              <Select
                label=""
                value={cat.kind}
                disabled={cat.is_system}
                onChange={(e) =>
                  run(
                    () => updateCategory.mutateAsync({
                      id: cat.id,
                      data: { kind: e.target.value as WithdrawalCategoryKind },
                    }),
                    'Tipo actualizado'
                  )
                }
                options={
                  cat.is_system
                    ? [{ value: 'supplier_payment', label: 'Pago a proveedor (del sistema)' }]
                    : kindOptions
                }
              />
            </div>

            <div className="flex items-center gap-2 ml-auto">
              {!cat.is_active && (
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500">Inactiva</span>
              )}
              {!cat.is_system && (
                <>
                  <button
                    onClick={() =>
                      run(
                        () => updateCategory.mutateAsync({ id: cat.id, data: { is_active: !cat.is_active } }),
                        cat.is_active ? 'Categoría desactivada' : 'Categoría activada'
                      )
                    }
                    className="text-xs px-2 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                  >
                    {cat.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                  <button
                    onClick={() => run(() => deleteCategory.mutateAsync(cat.id))}
                    disabled={deleteCategory.isPending}
                    title={cat.in_use ? 'Tiene salidas cargadas: se va a desactivar' : 'Eliminar'}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
